import {
  MUTATION_RESPONSE_RECEIVED,
  QUERY_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';
import { ResultStatus } from '@web-platform/shared-universal-constants/src/radar';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import {
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';

import { mapRailResults } from './mappers';

import type { ResultStatusType } from '@web-platform/shared-universal-types/src/network/endpoints/radar/ResultStatusType';
import type { RadarResponsePayload } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarResponsePayloadTypes';
import type { RadarRailResponse } from '@web-platform/shared-universal-types/src/network/endpoints/radar/rail';
import type { Action } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type { RailResultsState } from '@web-platform/shared-universal-types/src/redux/state/entities/rail';

export const INITIAL_STATE: RailResultsState = {
  results: { byBucket: {}, partnerLink: '' },
  moreDataExpected: false,
};

const notModifiedResultsStatus = new Set<ResultStatusType>([
  ResultStatus.COMPLETE_NOT_MODIFIED,
  ResultStatus.INCOMPLETE_NOT_MODIFIED,
]);

const incompleteResultStatus = new Set<ResultStatusType>([
  ResultStatus.INCOMPLETE,
  ResultStatus.INCOMPLETE_NOT_MODIFIED,
]);

export const railReducer = (
  state: RailResultsState = INITIAL_STATE,
  action: Action,
): RailResultsState => {
  switch (action.type) {
    case MUTATION_RESPONSE_RECEIVED:
    case QUERY_RESPONSE_RECEIVED: {
      const { response, type } = action.payload;

      if (type === RADAR_POLL || type === RADAR_CREATE_SESSIONS) {
        return handleRailResponse(response, state);
      }
      return state;
    }

    case SEARCH_PARAMS_CHANGED:
      return INITIAL_STATE;

    default:
      return state;
  }
};

const handleRailSection = (rail: RadarRailResponse, state: RailResultsState): RailResultsState => {
  const { status } = rail.context;
  if (status === ResultStatus.FAILURE) return state;
  if (notModifiedResultsStatus.has(status)) return state;

  const moreDataExpected = incompleteResultStatus.has(status);
  const { byBucket, partnerLink } = mapRailResults(rail);

  return {
    ...state,
    results: { byBucket, partnerLink },
    moreDataExpected,
  };
};

const handleRailResponse = (
  response: RadarResponsePayload,
  state: RailResultsState,
): RailResultsState => {
  if (!response?.body) return state;
  if (response.error || 'errors' in response.body) return state;
  if (response.body.context?.status === ResultStatus.FAILURE) return state;

  if ('rail' in response.body && response.body.rail) {
    return handleRailSection(response.body.rail, state);
  }

  return state;
};
